import { useLayoutEffect, useMemo } from 'react';
import { isValidNumber } from '../../helpers/numberHelpers';
import { Spacings, ElementStyling, Spacing } from './Grid.types';
import { isBoolean, isNumber, PickByType } from '../../helpers/typeHelpers';
import styles from './Grid.module.scss';

export const addDebouncedResizeHandler = (
    ref: React.RefObject<HTMLElement>,
    childSelector: string,
    callback: ($elem: HTMLElement) => void,
    repaintCallback?: ($elem: HTMLElement) => void,
    debounce = 10
) => {
    const $parent = ref.current;
    if (!$parent)
        return;

    let $children: NodeListOf<HTMLElement> | undefined = undefined;
    const findChildren = () => {
        $children = $parent.querySelectorAll<HTMLElement>(":scope>." + childSelector);
    };

    const invoke = (callback?: ($elem: HTMLElement) => void) => {
        if (!callback || !$children)
            return;

        for (let i = 0; i < $children.length; i++) {
            callback($children[i]);
        }
    };
    const invokeCallback = () => invoke(callback);
    const invokeRepaintCallback = () => invoke(repaintCallback);

    const handle = () => {
        findChildren();

        invokeCallback();
        requestAnimationFrame(invokeRepaintCallback);
    };

    let resizeDebounce: NodeJS.Timeout | undefined;
    const handler = () => {
        clearTimeout(resizeDebounce);
        resizeDebounce = setTimeout(handle, debounce);
    };

    let ro: ResizeObserver | undefined;
    const addHandler = () => {
        window.addEventListener("resize", handler);
        window.addEventListener("orientationchange", handler);
        if ($parent && typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => handler());
            ro.observe($parent);
        }
    };
    const removeHandler = () => {
        window.removeEventListener("resize", handler);
        window.removeEventListener("orientationchange", handler);
        ro?.disconnect();
        ro = undefined;
    };

    handle();
    addHandler();

    return removeHandler;
};

export const addSeparatorsResizeHandler = (gridRef: React.RefObject<HTMLElement>) => {
    const setOrientation = ($separator: HTMLElement) => {
        const $prevCol = $separator.previousElementSibling as HTMLElement;
        const $nextCol = $separator.nextElementSibling as HTMLElement;

        let orientation: "h" | "v" = "h";
        // If the separator is between two columns that are aligned horizontally, make it vertical
        if ($prevCol && $nextCol && $nextCol.offsetTop === $prevCol.offsetTop)
            orientation = "v";

        $separator.setAttribute("orientation", orientation);
    };

    const resetOrientation = ($separator: HTMLElement) => {
        $separator.removeAttribute("orientation");
    };


    return addDebouncedResizeHandler(gridRef, styles.sep, resetOrientation, setOrientation);
};

export const addRowMinHeightResizeHandler = (gridRef: React.RefObject<HTMLElement>) => {
    const setScrollState = ($row: HTMLElement) => {
        const cur = $row.clientHeight;
        const min = parseInt($row.getAttribute("min") || "0");
        const disableScroll = isValidNumber(min) && min > cur;
        $row.setAttribute("scroll", disableScroll ? "disable" : "");
    };
    return addDebouncedResizeHandler(gridRef, styles.row + "[min]", setScrollState);
};

export const useSeparatorsResizeHandler = (gridRef: React.RefObject<HTMLElement>) => useLayoutEffect(() => addSeparatorsResizeHandler(gridRef), [gridRef]);
export const useRowMinHeightResizeHandler = (gridRef: React.RefObject<HTMLElement>) => useLayoutEffect(() => addRowMinHeightResizeHandler(gridRef), [gridRef]);

export const getSpacing = (prop: string | number | symbol, value?: Spacings, result?: ElementStyling, suffix?: string) => {
    result = result ?? {
        attributes: {},
        style: {}
    };

    if (!value) {
        return result;
    }

    const name = prop as string;

    if (isBoolean(value)) {
        if (result)
            result.attributes[name] = suffix || "";
    }
    else if (isNumber(value)) {
        const key = ["--grid", name, suffix].filter(x => !!x).join("-");
        result.style[key] = value + "px";
    }
    else for (const key in value) {
        getSpacing(prop, value[key as keyof typeof value], result, key);
    }
    return result;
};

export const useSpacing = <T extends PickByType<T, Spacings | Spacing>,>(props: T, prop: keyof PickByType<T, Spacings | Spacing>) => {
    const value = props[prop];
    const result = useMemo(() => getSpacing(prop, value), [value, prop]);
    return result;
};
